import React from 'react';
import { Copy } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { toast } from 'sonner';

/**
 * MasterAccountBadge - Shows badges for consolidated bill accounts
 * [MA] for Master Account, [SC] for Sub-Account, [M] for legacy child accounts
 */
export const MasterAccountBadge = ({ 
  accountNo, 
  isChildAccount, 
  masterAccountNo, 
  isConsolidatedBill, 
  rowMarker, 
  dbSubCurrentBillAmount 
}) => {
  const handleCopyMaster = async (e) => {
    e.stopPropagation();
    if (masterAccountNo) {
      try {
        await navigator.clipboard.writeText(masterAccountNo);
        toast.success('Master account copied to clipboard');
      } catch {
        toast.error('Failed to copy');
      }
    }
  };

  // For consolidated bills, show [MA] or [SC] badges
  if (isConsolidatedBill) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs">{accountNo || '-'}</span>
        {rowMarker === 'MA' && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  variant="outline" 
                  className="h-4 px-1 text-[10px] font-semibold bg-purple-100 text-purple-700 border-purple-300 cursor-help"
                  data-testid="master-account-badge-ma"
                >
                  [MA]
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">Master Account</p>
                <p className="text-xs">Total Requested Amount calculated at this level</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {rowMarker === 'SC' && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  variant="outline" 
                  className="h-4 px-1 text-[10px] font-semibold bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200 cursor-pointer"
                  onClick={handleCopyMaster}
                  data-testid="sub-account-badge-sc"
                >
                  [SC]
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="space-y-1">
                <p className="font-semibold">Sub-Account</p>
                {masterAccountNo && <p className="text-xs">Master: {masterAccountNo}</p>}
                {dbSubCurrentBillAmount && <p className="text-xs">DB Amount: ${dbSubCurrentBillAmount.toFixed(2)}</p>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  // Legacy behavior for non-consolidated child accounts
  if (!isChildAccount || !masterAccountNo) {
    return <span className="text-xs">{accountNo || '-'}</span>;
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs">{accountNo}</span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className="h-4 px-1 text-[10px] font-semibold bg-blue-100 text-blue-700 border-blue-300 hover:bg-blue-200 cursor-pointer"
              onClick={handleCopyMaster}
              data-testid="master-account-badge"
            >
              M
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-2">
            <span>Master Account: {masterAccountNo}</span>
            <button 
              onClick={handleCopyMaster}
              className="p-1 hover:bg-muted rounded"
              title="Copy master account"
            >
              <Copy className="h-3 w-3" />
            </button>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default MasterAccountBadge;
